import React, { useCallback, useMemo } from 'react'
import { Form, Select } from 'antd'
import { SelectProps } from 'antd/es/select'
import styled from 'styled-components'
import LabelAndDescriptionFieldLayout from 'components/Assessments/components/FieldLayout/LabelAndDescriptionFieldLayout'
import QuestionContainer from 'components/Assessments/components/QuestionContainer'
import { BaseQuestionProps, QuestionLayout } from 'components/Assessments/components/types'
import { CodingSelectOption, CodingValue } from './types'
import {
  buildFormRules,
  createMultiSelectValueFromEvent,
  createMultiSelectValueProps,
  extractSecondaryText
} from './utils'

export interface CodingMultiSelectQuestionProps extends BaseQuestionProps {
  options: CodingSelectOption[]
  placeholder?: string
  loading?: boolean
  showSearch?: boolean
  allowClear?: boolean
  selectProps?: Omit<
    SelectProps,
    'mode' | 'options' | 'disabled' | 'placeholder' | 'loading' | 'showSearch' | 'allowClear'
  >
}

/**
 * Multi-select question component that handles CodingValue array conversion
 * Converts between string code arrays (for Select display) and { code, display } object arrays (for form storage)
 */
function CodingMultiSelectQuestion({
  field,
  formFieldName,
  layout = QuestionLayout.VERTICAL,
  disabled = false,
  required = false,
  customRules = [],
  options,
  placeholder = 'Type or select options',
  loading = false,
  showSearch = true,
  allowClear = false,
  selectProps
}: CodingMultiSelectQuestionProps): JSX.Element {
  const secondaryText = extractSecondaryText(field)
  const allRules = buildFormRules(required, field.required, customRules)

  // Get current form value to ensure selected values are always in options
  const currentValue = Form.useWatch(formFieldName) as CodingValue[] | undefined

  // Merge selected values into options to ensure they're always available for display
  const mergedOptions = useMemo(() => {
    if (!currentValue || !Array.isArray(currentValue)) {
      return options
    }

    // Find selected values that aren't in the current options
    const missingOptions: CodingSelectOption[] = currentValue
      .filter((item) => {
        if (typeof item === 'object' && item !== null && 'code' in item) {
          const code = String(item.code)
          return !options.some(
            (option) =>
              option.value.toLowerCase() === code.toLowerCase() ||
              option.code.toLowerCase() === code.toLowerCase()
          )
        }
        return false
      })
      .map((item) => {
        const code = String((item as CodingValue).code)
        const display = (item as CodingValue).display || code
        // Construct full label with ICD code prefix
        const fullLabel =
          code && display && code !== display ? `${code} - ${display}` : display || code
        return {
          label: fullLabel,
          value: code,
          code: code,
          display: display
        }
      })

    return [...options, ...missingOptions]
  }, [options, currentValue])

  const getValueFromEvent = useCallback(
    (codes: string[]) => createMultiSelectValueFromEvent(mergedOptions)(codes),
    [mergedOptions]
  )
  const getValueProps = useCallback(
    (value) => createMultiSelectValueProps(mergedOptions)(value),
    [mergedOptions]
  )

  return (
    <QuestionContainer linkId={field.linkId}>
      <Container>
        <LabelAndDescriptionFieldLayout
          label={field.text}
          secondaryText={secondaryText}
          layout={layout}
        >
          <Form.Item
            name={formFieldName}
            rules={allRules}
            style={{ margin: 0 }}
            getValueFromEvent={getValueFromEvent}
            getValueProps={getValueProps}
          >
            <Select
              mode="multiple"
              disabled={disabled}
              placeholder={placeholder}
              options={mergedOptions}
              loading={loading}
              showSearch={showSearch}
              allowClear={allowClear}
              {...selectProps}
            />
          </Form.Item>
        </LabelAndDescriptionFieldLayout>
      </Container>
    </QuestionContainer>
  )
}

const Container = styled.div`
  margin: 12px 0;
`

export default CodingMultiSelectQuestion
